/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { map } from 'lit/directives/map.js';
import { repeat } from 'lit/directives/repeat.js';
import { BaseProjectView } from './base-project-view.js';
import { projectJoinViewStyles } from './project-join-view-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-md-textarea/q1-md-textarea.js';
import '../../components/q1-member-chip/q1-member-chip.js';
import '../../components/q1-spinner-panel/q1-spinner-panel.js';

/*=== Q1 MODULES ===*/
import { FormHostMixin } from '../../modules/form-host-mixin.js';
import { q1VersionFunctions, q1DocFunctions } from '../../modules/q1-core-functions.js';
import { enrichMember } from '../../modules/member-helpers-client.js';
import {
  projectMemberRoleOptions, projectMemberRoleMap,
} from '../../models/project-member-client-enums.js';
import { projectJoinabilityMap } from '../../models/project-client-enums.js';
import { buildRefPathMap } from '../../modules/cache-helpers-client.js';

/*=== CONSTANTS ===*/

const PROJECT_MEMBER_JOIN_FLOW = 'projectMembers.request';

const ALL_MEMBERS_CACHE_ID = 'all-members-search';

const CACHE_MAX_AGE = 10; // minutes

/*=== VIEW CLASS ===*/

export class ProjectJoinView extends FormHostMixin(BaseProjectView) {
  static styles = [
    ...super.styles,
    projectJoinViewStyles,
  ];

  static properties = {
    ...super.properties,

    /**
     * Sub-view modes for the join view.
     * 
     * - `public-hidden`: Signed out and can't view project.
     * - `public-visible`: Signed out and CAN view project.
     * - `q1-hidden`: Signed in and can't view project.
     * - `q1-network-level`: Signed in and CAN view project, but is network level member.
     * - `q1-contributor-level`: Signed in and CAN view project, and is contributor+ level member.
     * - `pending-member`: Has proposed project member record.
     * - `inactive-member`: Has inactive project member record.
     * - `active-member`: Has active project member record.
     */
    loadedViewMode: { type: String },

    projectMember: { type: String },

    role: { type: String },
    proposerNote: { type: String },
    headline: { type: String },
    profile: { type: String },

    proposalStarted: { type: Boolean },
    processing: { type: Boolean },
  };

  constructor() {
    super();

    this.loadedViewMode = 'public-hidden';

    this.projectMember = null;

    this.role = projectMemberRoleOptions[0].value; // default to the first role option
    this.proposerNote = '';
    this.headline = '';
    this.profile = '';

    this.proposalStarted = false;
    this.processing = false;
  }

  /*=== LIFECYCLE ===*/

  async projectLoaded() {
    this.setViewMeta(`Join Project: ${this.project?.name} - q1 Network`);
    console.log('ProjectJoinView: projectLoaded', this.context);

    // Set the loaded view mode
    if (!this.isSignedIn) {
      this.loadedViewMode = this.context.canView ? 'public-visible' : 'public-hidden';
    } else if (this.context.isMember) {
      this.loadedViewMode = 'active-member';
    } else if (this.context.isProposedMember) {
      this.loadedViewMode = 'pending-member';

      await this._queryProjectMember();
      await this._queryOrganizers();
    } else if (this.currentMember.projectsInactive?.includes(this.project.id)) {
      this.loadedViewMode = 'inactive-member';

      await this._queryProjectMember();
      await this._queryOrganizers();
    } else if (!this.context.canView) {
      this.loadedViewMode = 'q1-hidden';
    } else if (this.currentMember.level === 'network') {
      this.loadedViewMode = 'q1-network-level';
    } else {
      this.loadedViewMode = 'q1-contributor-level';

      if (!this.canJoin) await this._queryOrganizers();
    }
  }

  /*=== ACTIONS ===*/

  startProposal() {
    this.proposalStarted = true;
  }

  cancelProposal() {
    this.proposalStarted = false;
  }

  async submitProposal() {
    if (!this.canJoin) return;

    const { proposeNewDoc } = q1VersionFunctions;

    const proposalForm = this.renderRoot.getElementById('proposal-form');
    if (!proposalForm.reportValidity()) {
      this.showToast(null, 'Please fill in all required fields to continue', 'danger');
      return;
    }
    if (!confirm(
      this.selectedJoinMode === 'open'
        ? 'Are you ready to join the project?'
        : 'Are you ready to submit your request to join the project?'
    )) return;

    this.processing = true;
    this.scrollToCenterOf('#join-card');
    
    try {
      const response = await proposeNewDoc({
        flowKey: PROJECT_MEMBER_JOIN_FLOW,
        relatedDocIds: {
          projectId: this.project.id,
          memberId: this.currentMember.id,
        },
        proposerNote: this.proposerNote,
        opParams: {
          role: this.role,
          headline: this.headline,
          profile: this.profile,
        },
      });

      const responseData = response.data || {};
      const { versionApprovalStatus } = responseData;
      const autoApproved = versionApprovalStatus === 'auto-approved';

      this.showToast(
        'It may take a few moments for everything to finish processing.',
        autoApproved ? 'Welcome to the project!' : 'Join request submitted!',
        'success',
        8000
      );
      this.scrollToCenterOf('q1-project-card');

      // Wait 2 seconds then redirect either to their profile or the project detail view.
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.navigateTo(autoApproved
        ? this.project.detailViewPath // if they're a member, take them straight to the project
        : this.currentProjectMemberProfilePath // else take them to their pending profile
      );
    } catch (error) {
      console.error('Error kicking off the join request flow:', error);
      this.showToast(error.message, 'Error processing your request.', 'danger');
    } finally {
      this.processing = false;
    }
  }

  /*=== HANDLERS ===*/

  // None yet

  /*=== GETTERS ===*/

  get docTitle_partiallyVisible() {
    return `Join Project: ${this.project.name} - q1 Network`;
  }

  get projectName() {
    return this.project?.name || 'this project';
  }
  
  /** Returns the path of the project invitation page. */
  get projectInvitePath() {
    if (!this.project?.detailViewPath) return '';

    return `${this.project.detailViewPath}/invite`;
  }

  /** Returns the id of the current member's project member record (whether or not it exists). */
  get currentProjectMemberId() {
    if (!this.currentMember?.id || !this.project?.id) return null;

    return `${this.project.id}:${this.currentMember.id}`;
  }

  /** Returns the path of the current member's project member profile (whether or not it exists). */
  get currentProjectMemberProfilePath() {
    if (!this.currentMember?.number || !this.project?.detailViewPath) return '';

    return `${this.project.detailViewPath}/@${this.currentMember.number}`;
  }

  get roleOptions() {
    // Hard-coding this for now instead of dynamically using the `requestable` property.
    const { supporter: supporterEnum, contributor: contributorEnum } = projectMemberRoleMap;
    
    const { supporterJoinMode, contributorJoinMode } = this.joinModeDetails;
    const supporterJoinEnum = projectJoinabilityMap[supporterJoinMode] || {};
    const contributorJoinEnum = projectJoinabilityMap[contributorJoinMode] || {};

    return [
      {
        value: supporterEnum.value,
        icon: supporterEnum.icon,
        iconLibrary: supporterEnum.iconLibrary,
        label: `${supporterEnum.label} (${supporterJoinEnum.label})`,
        disabled: !supporterJoinEnum.requestable,
      },
      {
        value: contributorEnum.value,
        icon: contributorEnum.icon,
        iconLibrary: contributorEnum.iconLibrary,
        label: `${contributorEnum.label} (${contributorJoinEnum.label})`,
        disabled: !contributorJoinEnum.requestable,
      },
    ];
  }

  /**
   * Returns a set of variables that makes the language determination code more readable.
   * These values are based on the settings, not the current context. They can help explain how 
   * this project works but don't include things like whether we're signed in, member level, etc.
   */
  get joinModeDetails() {
    const supporterJoinMode = this.project?.supporterJoinability || 'invite';
    const contributorJoinMode = this.project?.contributorJoinability || 'invite';
    
    const sameValue = supporterJoinMode === contributorJoinMode;
    const inviteOnly = sameValue && supporterJoinMode === 'invite';
    const bothOpen = sameValue && supporterJoinMode === 'open';
    const bothRequest = sameValue && supporterJoinMode === 'request';

    return {
      supporterJoinMode, contributorJoinMode,
      inviteOnly, bothOpen, bothRequest,
    };
  }

  get canJoin() {
    const joinActions = (this.context.allowedActions || []).filter(action => 
      action.startsWith('join-') || action.startsWith('request-')
    );
    
    return joinActions.length > 0;
  }

  get canInvite() {
    return this.allowedActions?.includes('invite') || false;
  }

  /**
   * Included when the project is visible to the member. Intended to explain in simple language
   * how project membership settings work for this project. Even if the member isn't signed in,
   * needs to upgrade to contributor, etc.
   */
  get joinModeExplainerText() {
    const {
      inviteOnly, bothOpen, bothRequest,
      supporterJoinMode, contributorJoinMode,
    } = this.joinModeDetails;

    // If both are the same then the language is simple.
    
    if (inviteOnly)
      return "This project may only be joined by invitation from the organizers.";
  
    if (bothOpen)
      return "This is an open project. "
        + "You can join directly as either a supporter or a contributor.";
    
    if (bothRequest)
      return "This project's membership is by request. "
        + "You can request to join as a supporter or contributor.";
    
    // Otherwise this is a hybrid situation.
    // Achieving casual language requires some logic.

    const supporterText = {
      open: 'you can join directly as a supporter',
      request: 'you can request to join as a supporter',
      invite: 'supporter membership is invitation only',
    }[supporterJoinMode];

    const contributorText = {
      open: 'you can join directly as a contributor',
      request: 'you can request to join as a contributor',
      invite: 'contributor membership is invitation only',
    }[contributorJoinMode];

    return `For this project, ${supporterText} and ${contributorText}.`;
  }

  /**
   * Only works once this.role is set.
   * Returns the join mode for the selected role: `open`, `request`, or `invite`.
   * Returns null if no role is selected.
   */
  get selectedJoinMode() {
    if (!this.role) return null;

    return this.role === 'supporter'
      ? this.project?.supporterJoinability
      : this.project?.contributorJoinability;
  }

  /** Only for once role is selected. Only relevant for `open` and `request`. */
  get proposerNoteLabel() {
    return this.selectedJoinMode === 'open'
      ? 'Join Note'
      : 'Join Request Note';
  }

  /** Only for once role is selected. Only relevant for `open` and `request`. */
  get proposerNoteHelpText() {
    return (this.selectedJoinMode === 'open'
      ? 'This note is visible to all members of the project. You can say whatever you like. '
      : 'This note is primarily for the project organizers reviewing your request to join, '
        + 'but will also be visible to other members of the project. '
    ) + 'You can use markdown formatting.';
  }

  /** Only for once role is selected. Only relevant for `open` and `request`. */
  get submitProposalButtonLabel() {
    return this.selectedJoinMode === 'open'
      ? 'Join the Project Now'
      : 'Submit Join Request';
  }

  get currentProjectMemberJoinFlow() {
    return this.projectMember?.joinFlow || 'none';
  }

  get yourMembershipButtonLabel() {
    if (this.loadedViewMode === 'pending-member')
      return this.currentProjectMemberJoinFlow === 'invite'
        ? 'Respond to Invitation'
        : 'Track Join Request';

    return 'View Your Profile';
  }

  get pendingMemberHeader() {
    if (this.currentProjectMemberJoinFlow === 'invite')
      return 'Your invitation to the project is waiting!';

    if (this.currentProjectMemberJoinFlow === 'request')
      return 'Your request to join the project is pending...';

    return 'Your project membership is pending...';
  }
  
  get pendingMemberText() {
    if (this.currentProjectMemberJoinFlow === 'invite')
      return 'To respond to the invitation right now click the button below. '
        + 'If you have any questions, you can reach out to the project organizers.';

    if (this.currentProjectMemberJoinFlow === 'request')
      return 'Click the button below to track the current status of your request. '
        + 'If you have any questions, you can reach out to the project organizers.';

    return 'Click the button below to track the current status of your membership. '
      + 'If you have any questions, you can reach out to the project organizers.';
  }

  get metaContext() {
    return { project: this.project };
  }  

  /*=== TEMPLATES ===*/
  
  get viewModeTemplate_loaded() {
    return html`
      ${this.loadedHeaderTemplate}

      ${this.project?.status !== 'live' ? '' : html`
        <div id="join-card-wrapper">
          <div id="join-card" class="sl-themed-secondary">
            <h2>Join the Project</h2>
            ${this.loadedBodyContentTemplate}
            
            <q1-spinner-panel
              label="Processing request..."
              ?open=${this.processing}
            ></q1-spinner-panel>
          </div>
        </div>
      `}
    `;
  }

  get loadedHeaderTemplate() {
    return html`
      <project-join-header>
        <q1-project-card .project=${this.project} always-active no-bg></q1-project-card>

        ${!this.project?.headline?.length ? '' : html`
          <div id="project-headline">
            ${this.project.headline}
          </div>
        `}
      </project-join-header>

      ${this.activationStatusPanelTemplate}
    `;
  }

  get activationStatusPanelTemplate() {
    if (this.project?.status === 'live') return null;

    return html`
      <activation-status-panel>
        <h2>${this.project?.status_label} Project</h2>
        <p>
          The project is currently at <strong>${this.project?.status_label}</strong> status, 
          which means it cannot be joined by new members. 
          
          The project must be published to enable joining.
        </p>
      </activation-status-panel>
    `;
  }

  get loadedBodyContentTemplate() {
    switch (this.loadedViewMode) {
      /**
       * NOTE: This should be an impossible state.
       * It is actually handled by the base project view class and rendered
       * via the not found template. But it's a good backup / default state.
       */
      case 'public-hidden':
        return html`
          ${this.bodyHeroTemplate}

          ${this.ctaSigninTemplate}
        `;
      
      case 'public-visible':
        return html`
          ${this.bodyHeroTemplate}

          ${this.joinModeExplainerTemplate}

          <sl-divider class="full-width"></sl-divider>

          ${this.ctaSigninTemplate}
        `;
        
      // Another impossible state, handled by base class not found template
      case 'q1-hidden':
        return html`
          <p class="subtitle hero primary">
            This page contains info about how to become a member of
            ${this.projectName}. This is a private project, so you'll need to reach out to the 
            organizers to get more information.
          </p>
        `;
        
      case 'q1-network-level':
        return html`
          ${this.bodyHeroTemplate}

          ${this.joinModeExplainerTemplate}

          <sl-divider class="full-width"></sl-divider>

          ${this.ctaContributorUpgradeTemplate}
        `;
      
      case 'q1-contributor-level':
        return html`
          ${this.bodyHeroTemplate}

          ${this.joinModeExplainerTemplate}

          <sl-divider class="full-width"></sl-divider>

          ${this.canJoin ? this.ctaProposalTemplate : html`
            <h3>Want to join?</h3>
      
            <p class="subtitle">
              Membership in this project is currently by invitation only.
              To get involved you should follow the project and participate in their community events.
              You can also reach out to the project organizers directly.
            </p>
            
            ${this.organizerListTemplate}
          `}
        `;
        
      case 'pending-member':
        return html`
          <p class="subtitle hero primary">
            This page contains info about how to become a member of
            ${this.projectName}. Your membership is currently pending! 
          </p>

          ${this.joinModeExplainerTemplate}

          <sl-divider class="full-width"></sl-divider>

          <h3>${this.pendingMemberHeader}</h3>
      
          <p class="subtitle">${this.pendingMemberText}</p>

          ${this.ctaYourMembershipButton}

          <sl-divider class="full-width"></sl-divider>

          ${this.organizerListTemplate}
        `;
        
      case 'inactive-member':
        return html`
          <p class="subtitle hero primary">
            This page contains info about how to become a member of
            ${this.projectName}. Your membership in this project is currently inactive.
          </p>

          <sl-divider class="full-width"></sl-divider>

          <h3>Interested in re-joining the project?</h3>
      
          <p class="subtitle">
            In order to re-join you'll need one of the organizers to invite you.
            Please reach out to the project organizers and coordinate with them.
          </p>

          ${this.ctaYourMembershipButton}

          <sl-divider class="full-width"></sl-divider>

          ${this.organizerListTemplate}
        `;
        
      case 'active-member':
        return html`
          <p class="subtitle hero primary">
            This page contains info about how to become a member of
            ${this.projectName}. You're already an active member!
          </p>

          ${this.joinModeExplainerTemplate}

          <sl-divider class="full-width"></sl-divider>

          <h3>Want to invite new project members?</h3>
      
          <p class="subtitle">
            You can use the project invite page to generate a join link
            ${this.canInvite ? 'or invite new members directly.' : 'to share with others.'}
          </p>

          ${this.ctaInviteButton}
        `;

      default:
        return html`
          <strong>
            Unknown view mode! ${this.loadedViewMode}
          </strong>
        `;
    }
  }

  get joinModeExplainerTemplate() {
    return html`
      <p class="subtitle explainer">
        <strong>
          <sl-icon name="manage_accounts" library="material"></sl-icon>
          Project Join Settings:
        </strong><br/>

        ${this.joinModeExplainerText}
      </p>
    `;
  }

  get bodyHeroTemplate() {
    return html`
      <p class="subtitle hero primary">
        This page contains info about how to become a member of
        ${this.projectName}. You can click the header above to browse the project or 
        use the links below to get started.
      </p>
    `;
  }

  get ctaSigninTemplate() {
    return html`
      <action-panel>
        <h3>Ready to get started?</h3>

        <p class="subtitle">
          The first step is joining the q1 Network.
        </p>

        <sl-button
          variant="primary"
          size="large"
          href="/register"
          pill
        >Register Now</sl-button>

        <p class="footer">
          Already a member?
          <a href="/signin">Sign in here</a>.
          <br/>
          Curious about the q1 Network?
          <a href="/join">Learn more about how it works</a>.
        </p>
      </action-panel>
    `;
  }

  get ctaContributorUpgradeTemplate() {
    return html`
      <action-panel>
        <h3>Ready to get started?</h3>

        <p class="subtitle">
          First you'll need to upgrade your membership to contributor level.
          The contributor level is about active collaboration and work.
        </p>

        <p class="subtitle">
          Becoming a contributor is fast and free.
        </p>

        <sl-button
          variant="primary"
          size="large"
          href="/m/contributor-upgrade"
          pill
        >
          <sl-icon
            name="communities"
            library="material"
            slot="prefix"
          ></sl-icon>
          Become a Contributor
        </sl-button>
      </action-panel>
    `;
  }

  get organizerListTemplate() {
    return html`
      <h4>The Project Organizers</h4>

      <organizers-list>
        ${map(this.organizers, member => html`
          <q1-member-chip
            .member=${member}
            target="_blank"
            extra-fields="number"
            icon=${member.level_icon}
            icon-tooltip=${member.level_label}
            signed-in show-linkedin
          ></q1-member-chip>
        `)}
      </organizers-list>
    `;
  }

  get ctaYourMembershipButton() {
    return html`
      <sl-button
        variant="primary"
        size="large"
        href=${this.currentProjectMemberProfilePath}
        pill
      >
        <sl-icon name="arrow_forward" library="material" slot="prefix"></sl-icon>
        ${this.yourMembershipButtonLabel}
      </sl-button>
    `;
  }

  get ctaInviteButton() {
    return html`
      <sl-button
        variant="primary"
        size="large"
        href=${this.projectInvitePath}
        pill
      >
        <sl-icon name="arrow_forward" library="material" slot="prefix"></sl-icon>
        Project Invitations
      </sl-button>
    `;
  }

  get ctaProposalTemplate() {
    if (this.proposalStarted) return this.submitProposalTemplate;

    return html`
      <h3>Ready to join?</h3>
      
      <p class="subtitle">
        Click the button below to select a project role and kick things off.
      </p>

      <sl-button
        variant="primary"
        size="large"
        @click=${this.startProposal}
        pill
      >
        <sl-icon name="keyboard_double_arrow_down" library="material" slot="prefix"></sl-icon>
        Show the Join Form
        <sl-icon name="keyboard_double_arrow_down" library="material" slot="suffix"></sl-icon>
      </sl-button>
    `;
  }
  
  get submitProposalTemplate() {
    return html`
      <form id="proposal-form" class="q1 sl-themed-secondary">
        
        <h3>Project Join Form</h3>

        <sl-radio-group
          id="role"
          name="role"
          label="Project Role"
          value=${this.role}
          @sl-change=${this.handleChange}
          help-text="Choose the project role you would like to join"
          required
        >
          ${this.roleOptions.map(({ value, label, icon, iconLibrary, disabled}) => html`
            <sl-radio-button value=${value} ?disabled=${disabled}>
              <sl-icon slot="prefix" library=${iconLibrary} name=${icon}></sl-icon>
              ${label}
            </sl-radio-button>
          `)}
        </sl-radio-group>

        <q1-md-textarea
          id="proposerNote"
          name="proposerNote"
          label=${this.proposerNoteLabel}
          help-text=${this.proposerNoteHelpText}
          @change=${this.handleChange}
          maxlength="2000"
          required
        ></q1-md-textarea>

        <h4>Your Project Profile</h4>

        <p class="subtitle hero">
          You'll also need to fill out an initial version of your project profile.
          You'll be able to update this as often as you like, but it's good to get something
          basic in place so you can be properly introduced
          to the other project members.
        </p>

        <sl-input
          id="profileHeadline"
          name="headline"
          label="Profile Headline"
          value=${this.headline || ''}
          @sl-change=${this.handleChange}
          maxlength="250"
          ?disabled=${this.processing}
          required
        ></sl-input>

        <q1-md-textarea
          id="profileBody"
          name="profile"
          label="Profile Body"
          value=${this.profile || ''}
          @change=${this.handleChange}
          maxlength="2000"
          rows="15"
          resize="auto"
          ?disabled=${this.responding}
          required
        ></q1-md-textarea>

        <button-bar class="sl-themed-secondary right-align">
          <sl-button
            size="large"
            @click=${this.cancelProposal}
          >Cancel</sl-button>

          <sl-button
            id="submit-proposal"
            variant="primary"
            size="large"
            ?disabled=${this.processing}
            @click=${this.submitProposal}
          >
            <sl-icon slot="prefix" library="material" name="send"></sl-icon>
            ${this.submitProposalButtonLabel}
          </sl-button>
        </button-bar>
      </form>
    `;
  }

  /*=== UTILITY METHODS ===*/
  
  /** Sets up `this.projectMember` */
  async _queryProjectMember() {
    const { getDoc } = q1DocFunctions;

    const response = await getDoc({
      docCollection: 'projectMembers',
      docId: this.currentProjectMemberId,
    });
    this.projectMember = response.data.docData;
  }

  /** Sets up `this.organizers` */
  async _queryOrganizers() {
    let allMembersCache = 
      await this.appElement.cacheManager.getCache(ALL_MEMBERS_CACHE_ID, CACHE_MAX_AGE);
    if (allMembersCache.isNewOrStale) allMembersCache = await allMembersCache.refreshComplete;
    const memberPathMap = buildRefPathMap(allMembersCache.data, 'members', 'members');
    this.organizers = (this.project.organizers || [])
      .map(organizerPath => enrichMember(memberPathMap[organizerPath]))
      .filter(o => o);
  }

}

customElements.define('project-join-view', ProjectJoinView);
